import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Dialog,
  DialogContent,
  FormControlLabel,
  ModalProps,
  Switch,
  Typography,
} from '@mui/material';
import { Grid } from '@mui/system';
import { BackupCodesStatus, SsoService, TenantEmailDiscoveryResponse, TotpService } from 'api';
import { firmsAction } from 'common/actions';
import { ERROR_404 } from 'common/config/api';
import {
  CANNOT_UPDATE_2FA,
  DONE_CODES,
  PAGE_TITLE,
  REAUTHENTICATION_MESSAGE,
  TWO_FA_ORG_MANDATORY_HINT
} from 'common/constants/auth';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { useSecondaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import { BackupCodes, OTP as Otp, SignOutButton } from 'pages/Auth/components';
import type { DeviceData } from 'pages/Auth/components/OTP';
import { setStoredAccessToken, setStoredRefreshToken } from 'pages/Auth/utils';
import useAccountSettingsNavItems from 'pages/UserProfile/useAccountSettingsNavItems';
import { useResponse } from 'services/hooks';
import { Logger } from 'utilities';
import BackupCodesStatusSummary from './components/BackupCodesStatusSummary';
import { User2FAStatus } from './User2FA.props';
import { StyledFormGroup, StyledHeading, StyledMessage, StyledMessageContainer } from './User2FA.styles';

const User2FA = () => {
  const [user2faStatus, setUser2faStatus] = useState<User2FAStatus>({
    user_has_totp_device: false,
    confirmed: false,
    persistent_id: '',
  });
  const [backupCodesStatus, setBackupCodesStatus] = useState<BackupCodesStatus | null>(null);
  const [isLoadingBackupCodesStatus, setIsLoadingBackupCodesStatus] = useState(false);
  // Raw codes only ever live here for the "just generated" reveal-once moment.
  // null = show the persistent status summary; non-null (even []) = show the reveal.
  const [freshBackupCodes, setFreshBackupCodes] = useState<string[] | null>(null);
  const [showOTP, setShowOTP] = useState(false);
  const [isGeneratingBackupCodes, setIsGeneratingBackupCodes] = useState(false);
  const [displayReauthenticate, setDisplayReauthenticate] = useState(false);
  const [isTwoFactorMandatory, setIsTwoFactorMandatory] = useState(false);
  const [{user}] = useStore() as unknown as UseStoreValues;
  const accountSettingsNavItems = useAccountSettingsNavItems();

  const {processErrorResponse} = useResponse();

  const handleDialogClose: ModalProps['onClose'] = (_event, reason) => {
    if (reason && reason === 'backdropClick') {
      setShowOTP(false);
    }
  };

  const handle2faChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const {checked} = event.target;
    const isDisablingMandatoryTwoFactor = isTwoFactorMandatory && !checked;

    if (isDisablingMandatoryTwoFactor) {
      return;
    }

    // Launch the OTP dialog if the user is setting up 2FA for the first time
    if (!user2faStatus.user_has_totp_device && checked) {
      setShowOTP(true);
      return;
    }

    const updateTOTP = async () => {
      try {
        if (!checked) {
          const response = await TotpService.apiTotpDeleteCreate();
          setStoredAccessToken(response.token.access);
          setStoredRefreshToken(response.token.refresh);
          setUser2faStatus({
            user_has_totp_device: false,
            confirmed: false,
          });
        } else {
          setShowOTP(true);
        }
      } catch (err) {
        processErrorResponse({
          error: err,
          defaultErrorMessage: CANNOT_UPDATE_2FA,
        });
        // Revert the switch status to the previous state if there was an error
        setUser2faStatus(prevStatus => ({
          ...prevStatus,
          confirmed: !checked,
        }));
      }
    };

    if (user2faStatus.user_has_totp_device) {
      updateTOTP();
    }
  };

  const generateNewBackupCodes = useCallback(async () => {
    setIsGeneratingBackupCodes(true);

    try {
      const response = await TotpService.apiTotpStaticCreateCreate();
      if (response) {
        setFreshBackupCodes(response.tokens);
        // Optimistic status update so the summary is correct even before the
        // reveal is dismissed; corrected for real by fetchBackupCodesStatus().
        setBackupCodesStatus({
          codes_remaining: response.tokens.length,
          codes_total: response.tokens.length,
          last_generated_at: new Date().toISOString(),
        });
      }
    } catch (error: unknown) {
      // If we receive a Forbidden error we need to reauthenticate the user
      if ((error as { status?: number })?.status === 403) {
        setDisplayReauthenticate(true);
      }
      Logger.error('Error generating backup codes', error);
    } finally {
      setIsGeneratingBackupCodes(false);
    }
  }, []);

  const secondaryNavigation = useMemo(() => ({
    title: 'Account',
    items: accountSettingsNavItems,
  }), [accountSettingsNavItems]);

  useSecondaryNavigationRegistration(secondaryNavigation);

  // Settings must mirror the login rule: only SAML-only tenants can opt out of app-managed 2FA.
  useEffect(() => {
    let isMounted = true;

    const loadTwoFactorPolicy = async () => {
      try {
        const [firmListResponse, discoveryResponse] = await Promise.all([
          firmsAction.getFirmList(),
          user?.email ? SsoService.apiSsoDiscoverCreate({email: user.email}) : Promise.resolve(null),
        ]);

        if (!isMounted) {
          return;
        }

        const organizationRequiresTwoFactor = Boolean(firmListResponse?.requires_2fa);
        const usesSamlOnlyFlow = discoveryResponse?.post_discovery_flow === TenantEmailDiscoveryResponse.post_discovery_flow.SAML;

        setIsTwoFactorMandatory(organizationRequiresTwoFactor || !usesSamlOnlyFlow);
      } catch {
        if (!isMounted) {
          return;
        }

        setIsTwoFactorMandatory(true);
      }
    };

    if (user?.id) {
      loadTwoFactorPolicy();
    }

    return () => {
      isMounted = false;
    };
  }, [user?.email, user?.id]);

  useEffect(() => {
    const getUserTOTP = async () => {
      try {
        const response = await TotpService.apiTotpGetList();

        if (response) {
          setUser2faStatus(response);
        }
      } catch (error: unknown) {
        Logger.error('Error getting TOTP', error);
        throw new Error(error as string);
      }
    };

    if (user) {
      getUserTOTP();
    }
  }, [user]);

  const fetchBackupCodesStatus = useCallback(async () => {
    setIsLoadingBackupCodesStatus(true);
    try {
      const response = await TotpService.apiTotpStaticGetList();
      if (response) {
        setBackupCodesStatus(response);
      }
    } catch (error: unknown) {
      // A 404 ("no static device") just means zero codes exist yet - it is not
      // a signal to auto-generate. The user must click "Generate" explicitly.
      if ((error as { status?: number })?.status === Number(ERROR_404)) {
        setBackupCodesStatus({ codes_remaining: 0, codes_total: 0, last_generated_at: null });
      } else {
        Logger.error('Error getting backup codes status', error);
      }
    } finally {
      setIsLoadingBackupCodesStatus(false);
    }
  }, []);

  useEffect(() => {
    /* Handles 2 cases:
    1. Page load for a returning user (fire immediately).
    2. Enrollment (when the dialog closes the backup codes
    are guaranteed to exist so it's safe to fetch them).
    */
    if (user2faStatus.confirmed && !showOTP) {
      fetchBackupCodesStatus();
    }
  }, [fetchBackupCodesStatus, showOTP, user2faStatus.confirmed]);

  const handleDismissFreshBackupCodes = useCallback(() => {
    setFreshBackupCodes(null);
    fetchBackupCodesStatus();
  }, [fetchBackupCodesStatus]);

  const setOtpDeviceData = useCallback((updateDeviceData: (prevData: DeviceData) => DeviceData) => {
    setUser2faStatus(prevStatus => ({
      ...prevStatus,
      ...updateDeviceData(prevStatus),
    }));
  }, []);

  const handleOtpVerified = useCallback((verified: boolean) => {
    if (verified) {
      setShowOTP(false);
    }
  }, []);

  return (
    <>
      <Accordion sx={{ pt: 1 }}>
        <AccordionSummary className="accordionSummary" expandIcon={<ExpandMoreIcon className="icon"/>}>
          <StyledHeading>Activate/Deactivate {PAGE_TITLE}</StyledHeading>
        </AccordionSummary>
        <AccordionDetails>
          {isTwoFactorMandatory ? (
            <Typography color="textSecondary" sx={{pb: 1.5}} variant="body2">
              {TWO_FA_ORG_MANDATORY_HINT}
            </Typography>
          ) : null}
          <StyledFormGroup row>
            <FormControlLabel
              control={
                <Switch
                  checked={user2faStatus.confirmed}
                  onChange={handle2faChange}
                  name="user2fa"
                  color="primary"
                  disabled={isTwoFactorMandatory && user2faStatus.confirmed}
                  disableRipple
                />
              }
              label={
                <Typography variant="body2" color="textPrimary">
                                    Enable {PAGE_TITLE}
                </Typography>
              }
              labelPlacement="start"
            />
          </StyledFormGroup>
        </AccordionDetails>
      </Accordion>
      {displayReauthenticate
        ? user2faStatus?.confirmed && (
          <StyledMessageContainer alignItems="center" container>
            <Grid>
              <StyledMessage>{REAUTHENTICATION_MESSAGE}</StyledMessage>
            </Grid>

            <Grid>
              <SignOutButton/>
            </Grid>
          </StyledMessageContainer>
        )
        : user2faStatus?.confirmed && (
          <Accordion>
            <AccordionSummary className="accordionSummary" expandIcon={<ExpandMoreIcon className="icon"/>}>
              <StyledHeading>{PAGE_TITLE} Backup Codes</StyledHeading>
            </AccordionSummary>

            <AccordionDetails>
              {freshBackupCodes ? (
                <BackupCodes
                  backupCodes={freshBackupCodes}
                  disabledGenerate={!user2faStatus?.confirmed}
                  onGenerateNewCodes={generateNewBackupCodes}
                  onPrimaryAction={handleDismissFreshBackupCodes}
                  primaryActionLabel={DONE_CODES}
                  isGeneratingBackupCodes={isGeneratingBackupCodes}
                  username={user?.email}
                />
              ) : (
                <BackupCodesStatusSummary
                  status={backupCodesStatus}
                  isLoading={isLoadingBackupCodesStatus}
                  isGeneratingBackupCodes={isGeneratingBackupCodes}
                  disabledGenerate={!user2faStatus?.confirmed}
                  onGenerateNewCodes={generateNewBackupCodes}
                />
              )}
            </AccordionDetails>
          </Accordion>
        )}
      <Dialog fullWidth maxWidth="sm" open={showOTP} onClose={handleDialogClose}>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Otp
            showMandatoryHint={isTwoFactorMandatory}
            userHas2FA={false}
            showActionButtons={false}
            setDeviceData={setOtpDeviceData}
            setDisplayReauthenticate={setDisplayReauthenticate}
            verifyOtp={handleOtpVerified}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default User2FA;
